'use client';

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useRouter } from 'next/navigation';
import { Input } from '@/components/ui/input';
import { Phone } from 'lucide-react';
import {
  isAndroidMobile,
  generateRequestNonce,
  triggerTruecallerDeepLink,
  detectTruecallerApp,
  pollTruecallerLoginStatus,
  type TruecallerLoginResult,
} from '@/lib/truecaller';
import {
  sendOTP,
  verifyOTP,
  initializeRecaptcha,
  clearRecaptcha,
} from '@/lib/firebase';
import { authApi } from '@/lib/medusa-client';

interface TruecallerLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  returnUrl?: string;
}

interface ConfirmData {
  name: string;
  phone: string;
  customer: NonNullable<TruecallerLoginResult['customer']>;
  token: string;
}

type State =
  | { phase: 'detecting' }
  | { phase: 'polling' }
  | { phase: 'confirm'; data: ConfirmData }
  | { phase: 'hidden' }
  | { phase: 'phone' }
  | { phase: 'name' }
  | { phase: 'otp' };

/**
 * TruecallerLoginModal - 2-phase Material Depot style flow
 * 
 * Phase 1: Auto-trigger deep link (TC native popup - unavoidable OS consent)
 * Phase 2: Show "Hi, [Name] / USE [Phone]" bottom sheet
 * 
 * Fallback: If TC not installed or user clicks "Use Another Number", show OTP flow
 */
export function TruecallerLoginModal({ isOpen, onClose, onSuccess, returnUrl }: TruecallerLoginModalProps) {
  const [state, setState] = useState<State>({ phase: 'detecting' });
  const [visible, setVisible] = useState(false);
  const queryClient = useQueryClient();
  const { checkAuth } = useAuth();
  const { toast } = useToast();
  const router = useRouter();

  // OTP flow states
  const [phoneNumber, setPhoneNumber] = useState('');
  const [userName, setUserName] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [isNewCustomer, setIsNewCustomer] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    
    // On desktop, redirect to login page
    if (typeof window !== 'undefined' && window.innerWidth >= 768) {
      onClose();
      router.push(`/login?returnUrl=${encodeURIComponent(returnUrl || window.location.pathname)}`);
      return;
    }
    
    // Only run on Android mobile
    if (!isAndroidMobile()) {
      // Fallback to phone input on non-Android mobile
      setState({ phase: 'phone' });
      setVisible(true);
      return;
    }

    let cancelled = false;

    const run = async () => {
      const nonce = generateRequestNonce();

      // 1. Fire deep link → TC app opens its native consent popup
      triggerTruecallerDeepLink(nonce);

      // 2. Wait 800ms then check if TC app opened
      const appDetected = await detectTruecallerApp();
      if (cancelled) return;

      if (!appDetected) {
        // TC not installed - show phone input
        setState({ phase: 'phone' });
        setVisible(true);
        return;
      }

      // 3. TC is handling consent, poll in background
      setState({ phase: 'polling' });

      const result = await pollTruecallerLoginStatus(nonce);
      if (cancelled) return;

      if (result.success && result.customer) {
        // 4. Show confirmation sheet with user's name and phone
        const rawPhone = result.customer.phone || '';
        const digits = rawPhone.replace(/\D/g, '');
        const displayPhone = digits.length > 10 ? digits.slice(-10) : digits;

        setState({
          phase: 'confirm',
          data: {
            name: result.customer.first_name || 'there',
            phone: displayPhone || rawPhone,
            customer: result.customer,
            token: (result as any).token || '',
          },
        });
        setVisible(true);
      } else {
        // Failed: fall back to phone input
        setState({ phase: 'phone' });
        setVisible(true);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [isOpen]);

  // "USE [phone]" tapped: complete login
  const handleUse = useCallback(async () => {
    if (state.phase !== 'confirm') return;
    try {
      await checkAuth();
      queryClient.invalidateQueries({ queryKey: ['/api/cart'] });
      toast({
        title: `Welcome, ${state.data.name}!`,
        description: 'Logged in successfully',
      });
      setState({ phase: 'hidden' });
      setVisible(false);
      
      if (onSuccess) {
        onSuccess();
      } else if (returnUrl) {
        router.push(returnUrl);
      }
      
      onClose();
    } catch {
      toast({ title: 'Login error', description: 'Please try OTP', variant: 'destructive' });
      setState({ phase: 'phone' });
    }
  }, [state, checkAuth, queryClient, toast, onSuccess, returnUrl, router, onClose]);

  const handleUseAnother = useCallback(() => {
    setState({ phase: 'phone' });
  }, []);

  const formatPhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    return cleaned.slice(0, 10);
  };

  const isPhoneValid = phoneNumber.length === 10 && /^\d{10}$/.test(phoneNumber);

  const startResendTimer = () => {
    setResendTimer(30);
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!isPhoneValid) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    setLoading(true);

    try {
      await sendOTP(phoneNumber);
      const response = await authApi.sendOtp(phoneNumber);
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to send OTP');
        setLoading(false);
        return;
      }

      setIsNewCustomer(data.isNewCustomer);

      if (data.isNewCustomer) {
        setState({ phase: 'name' });
      } else {
        setState({ phase: 'otp' });
        startResendTimer();
      }
    } catch (error: any) {
      setError(error.message || 'Failed to send OTP');
      await clearRecaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handleNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!userName.trim()) {
      setError('Please enter your name');
      return;
    }

    setState({ phase: 'otp' });
    startResendTimer();
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }

    if (newOtp.every(digit => digit) && index === 5) {
      handleOtpSubmit(newOtp.join(''));
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleOtpSubmit = async (otpValue?: string) => {
    const finalOtp = otpValue || otp.join('');
    if (finalOtp.length !== 6) {
      setError('Please enter the complete OTP');
      return;
    }

    setLoading(true);

    try {
      const idToken = await verifyOTP(finalOtp);
      await loginWithFirebase(idToken, isNewCustomer ? userName : undefined);
      queryClient.invalidateQueries({ queryKey: ['/api/cart'] });
      
      toast({
        title: 'Welcome!',
        description: 'Logged in successfully',
      });
      
      setState({ phase: 'hidden' });
      setVisible(false);
      
      if (onSuccess) {
        onSuccess();
      } else if (returnUrl) {
        router.push(returnUrl);
      }
      
      onClose();
    } catch (error: any) {
      setError(error.message || 'Failed to verify OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;

    setLoading(true);
    setError('');

    try {
      await sendOTP(phoneNumber);
      startResendTimer();
      setOtp(['', '', '', '', '', '']);
      toast({
        title: 'OTP Sent',
        description: 'A new OTP has been sent to your phone',
      });
    } catch (error: any) {
      setError(error.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && (state.phase === 'phone' || state.phase === 'name' || state.phase === 'otp')) {
      initializeRecaptcha('recaptcha-container-modal').catch(console.error);
    }
    return () => {
      clearRecaptcha().catch(console.error);
    };
  }, [visible, state.phase]);

  const { loginWithFirebase } = useAuth();

  if (!isOpen || !visible) return null;

  // Phone Input UI
  if (state.phase === 'phone') {
    return (
      <>
        <div
          className="fixed inset-0 z-40"
          style={{ background: 'rgba(0,0,0,0.55)' }}
          onClick={onClose}
          aria-hidden
        />

        <div
          role="dialog"
          aria-modal="true"
          aria-label="Login"
          className="fixed bottom-0 left-0 right-0 z-50 bg-white animate-slide-up"
          style={{
            borderRadius: '18px 18px 0 0',
            maxWidth: '480px',
            margin: '0 auto',
            boxShadow: '0 -8px 40px rgba(0,0,0,0.18)',
          }}
        >
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 rounded-full bg-gray-300" />
          </div>

          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-400"
            aria-label="Close"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <line x1="1" y1="1" x2="9" y2="9" /><line x1="9" y1="1" x2="1" y2="9" />
            </svg>
          </button>

          <div className="flex items-center justify-center pt-4 pb-4 px-6">
            <img
              src="/hinch-logo.svg"
              alt="HINCH"
              className="h-8 w-auto object-contain"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          </div>

          <div className="h-[2px] bg-orange-500 mx-6 rounded-full mb-5" />

          <div className="px-6 pb-6">
            <h2 className="text-[22px] font-bold text-gray-900 mb-1">
              Enter Mobile Number
            </h2>
            <p className="text-sm text-gray-500 mb-6 leading-relaxed">
              We'll send you an OTP to verify
            </p>

            <form onSubmit={handlePhoneSubmit}>
              <div className="mb-4">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 text-sm font-medium">+91</span>
                  </div>
                  <Input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => {
                      const formatted = formatPhoneNumber(e.target.value);
                      setPhoneNumber(formatted);
                      if (error) setError('');
                    }}
                    className="pl-12 h-12 text-base border-gray-300 rounded-xl"
                    placeholder="Enter mobile number"
                    maxLength={10}
                    required
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    {phoneNumber.length > 0 && isPhoneValid ? (
                      <div className="h-5 w-5 text-green-500">✓</div>
                    ) : (
                      <Phone className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </div>
                {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
              </div>

              <button
                type="submit"
                disabled={loading || !isPhoneValid}
                className="w-full flex items-center justify-center py-4 mb-4 text-white font-bold text-base rounded-full transition-opacity active:opacity-80 disabled:opacity-50"
                style={{ background: '#F9BC06' }}
              >
                {loading ? 'Sending OTP...' : 'Get OTP'}
              </button>
            </form>
          </div>

          <div id="recaptcha-container-modal" className="hidden"></div>

          <div className="flex items-center justify-center gap-2 py-3 border-t border-gray-100" style={{ background: '#fafafa' }}>
            <span className="text-[11px] text-gray-400 font-medium">100% secure authentication</span>
          </div>
          <div style={{ paddingBottom: 'env(safe-area-inset-bottom)', background: '#fafafa' }} />
        </div>

        <style jsx global>{`
          @keyframes slide-up {
            from { transform: translateY(100%); opacity: 0; }
            to   { transform: translateY(0);    opacity: 1; }
          }
          .animate-slide-up { animation: slide-up 0.28s cubic-bezier(0.32,0.72,0,1) forwards; }
        `}</style>
      </>
    );
  }

  // Name Input UI
  if (state.phase === 'name') {
    return (
      <>
        <div
          className="fixed inset-0 z-40"
          style={{ background: 'rgba(0,0,0,0.55)' }}
          onClick={onClose}
          aria-hidden
        />

        <div
          role="dialog"
          aria-modal="true"
          className="fixed bottom-0 left-0 right-0 z-50 bg-white animate-slide-up"
          style={{
            borderRadius: '18px 18px 0 0',
            maxWidth: '480px',
            margin: '0 auto',
            boxShadow: '0 -8px 40px rgba(0,0,0,0.18)',
          }}
        >
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 rounded-full bg-gray-300" />
          </div>

          <button
            onClick={() => setState({ phase: 'phone' })}
            className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-400"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <line x1="1" y1="1" x2="9" y2="9" /><line x1="9" y1="1" x2="1" y2="9" />
            </svg>
          </button>

          <div className="flex items-center justify-center pt-4 pb-4 px-6">
            <img src="/hinch-logo.svg" alt="HINCH" className="h-8 w-auto object-contain" />
          </div>

          <div className="h-[2px] bg-orange-500 mx-6 rounded-full mb-5" />

          <div className="px-6 pb-6">
            <h2 className="text-[22px] font-bold text-gray-900 mb-1">Tell us your name</h2>
            <p className="text-sm text-gray-500 mb-6 leading-relaxed">Let us know what to call you</p>

            <form onSubmit={handleNameSubmit}>
              <div className="mb-4">
                <Input
                  type="text"
                  value={userName}
                  onChange={(e) => {
                    setUserName(e.target.value);
                    if (error) setError('');
                  }}
                  className="h-12 text-base border-gray-300 rounded-xl"
                  placeholder="Enter your full name"
                  required
                />
                {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
              </div>

              <button
                type="submit"
                disabled={loading || !userName.trim()}
                className="w-full py-4 text-white font-bold text-base rounded-full transition-opacity active:opacity-80 disabled:opacity-50"
                style={{ background: '#F9BC06' }}
              >
                Continue
              </button>
            </form>
          </div>

          <div style={{ paddingBottom: 'env(safe-area-inset-bottom)', background: '#fafafa' }} />
        </div>

        <style jsx global>{`
          @keyframes slide-up {
            from { transform: translateY(100%); opacity: 0; }
            to   { transform: translateY(0);    opacity: 1; }
          }
          .animate-slide-up { animation: slide-up 0.28s cubic-bezier(0.32,0.72,0,1) forwards; }
        `}</style>
      </>
    );
  }

  // OTP Input UI
  if (state.phase === 'otp') {
    return (
      <>
        <div
          className="fixed inset-0 z-40"
          style={{ background: 'rgba(0,0,0,0.55)' }}
          onClick={onClose}
          aria-hidden
        />

        <div
          role="dialog"
          aria-modal="true"
          className="fixed bottom-0 left-0 right-0 z-50 bg-white animate-slide-up"
          style={{
            borderRadius: '18px 18px 0 0',
            maxWidth: '480px',
            margin: '0 auto',
            boxShadow: '0 -8px 40px rgba(0,0,0,0.18)',
          }}
        >
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 rounded-full bg-gray-300" />
          </div>

          <button
            onClick={() => setState({ phase: 'phone' })}
            className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-400"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <line x1="1" y1="1" x2="9" y2="9" /><line x1="9" y1="1" x2="1" y2="9" />
            </svg>
          </button>

          <div className="flex items-center justify-center pt-4 pb-4 px-6">
            <img src="/hinch-logo.svg" alt="HINCH" className="h-8 w-auto object-contain" />
          </div>

          <div className="h-[2px] bg-orange-500 mx-6 rounded-full mb-5" />

          <div className="px-6 pb-6">
            <h2 className="text-[22px] font-bold text-gray-900 mb-1">Verify your number</h2>
            <p className="text-sm text-gray-500 mb-6 leading-relaxed">
              We've sent a code to +91 {phoneNumber}
            </p>

            <div className="mb-4">
              <div className="flex justify-between gap-2 mb-4">
                {otp.map((digit, index) => (
                  <Input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    className="w-full h-12 text-center text-lg font-semibold border-gray-300 rounded-xl"
                    required
                  />
                ))}
              </div>
              {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
            </div>

            <button
              onClick={() => handleOtpSubmit()}
              disabled={loading || otp.some(digit => !digit)}
              className="w-full py-4 mb-4 text-white font-bold text-base rounded-full transition-opacity active:opacity-80 disabled:opacity-50"
              style={{ background: '#F9BC06' }}
            >
              {loading ? 'Verifying...' : 'Verify & Login'}
            </button>

            <div className="text-center">
              <p className="text-sm text-gray-500">
                Didn't receive the code?{' '}
                <button
                  onClick={handleResendOtp}
                  disabled={resendTimer > 0}
                  className={`font-medium ${resendTimer > 0 ? 'text-gray-400 cursor-not-allowed' : 'text-orange-600'}`}
                >
                  {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                </button>
              </p>
            </div>
          </div>

          <div id="recaptcha-container-modal" className="hidden"></div>
          <div style={{ paddingBottom: 'env(safe-area-inset-bottom)', background: '#fafafa' }} />
        </div>

        <style jsx global>{`
          @keyframes slide-up {
            from { transform: translateY(100%); opacity: 0; }
            to   { transform: translateY(0);    opacity: 1; }
          }
          .animate-slide-up { animation: slide-up 0.28s cubic-bezier(0.32,0.72,0,1) forwards; }
        `}</style>
      </>
    );
  }

  // Truecaller Confirmation UI (Phase 2)
  if (state.phase !== 'confirm') return null;

  const { name, phone } = state.data;

  return (
    <>
      <div
        className="fixed inset-0 z-40"
        style={{ background: 'rgba(0,0,0,0.55)' }}
        onClick={handleUseAnother}
        aria-hidden
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Truecaller verification"
        className="fixed bottom-0 left-0 right-0 z-50 bg-white animate-slide-up"
        style={{
          borderRadius: '18px 18px 0 0',
          maxWidth: '480px',
          margin: '0 auto',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.18)',
        }}
      >
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>

        <button
          onClick={handleUseAnother}
          className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-400"
          aria-label="Close"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <line x1="1" y1="1" x2="9" y2="9" /><line x1="9" y1="1" x2="1" y2="9" />
          </svg>
        </button>

        <div className="flex items-center justify-center pt-4 pb-4 px-6">
          <img
            src="/hinch-logo.svg"
            alt="HINCH"
            className="h-8 w-auto object-contain"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        </div>

        <div className="h-[2px] bg-orange-500 mx-6 rounded-full mb-5" />

        <div className="px-6 pb-2">
          <h2 className="text-[22px] font-bold text-gray-900 mb-1">
            Hi, <span className="text-orange-600">{name}</span>
          </h2>
          <p className="text-sm text-gray-500 mb-6 leading-relaxed">
            To get started, please verify mobile number
          </p>

          <button
            type="button"
            onClick={handleUse}
            className="w-full flex items-center justify-center py-4 mb-4 text-white font-bold text-base rounded-full transition-opacity active:opacity-80"
            style={{ background: '#F9BC06' }}
          >
            USE {phone}
          </button>

          <button
            type="button"
            onClick={handleUseAnother}
            className="w-full text-center py-3 text-xs font-bold uppercase tracking-widest"
            style={{ color: '#F9BC06', letterSpacing: '0.09em' }}
          >
            Use Another Mobile Number
          </button>

          <p className="text-[11px] text-gray-400 text-center mt-1 mb-4 leading-relaxed px-2">
            By continuing you consent to share your name and number with{' '}
            <span className="font-medium text-gray-600">Hinch</span>, and agree to the{' '}
            <a href="/privacy" className="underline text-gray-500">privacy policy</a> and{' '}
            <a href="/terms" className="underline text-gray-500">terms of service</a>
          </p>
        </div>

        <div className="flex items-center justify-center gap-2 py-3 border-t border-gray-100" style={{ background: '#fafafa' }}>
          <span className="text-[11px] text-gray-400 font-medium">Instant Verification by</span>
          <svg width="78" height="14" viewBox="0 0 117 22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Truecaller">
            <path d="M13.2 4.4H8.6V18H6V4.4H1.4V2H13.2V4.4Z" fill="#009688" />
            <path d="M21.1 18.2C19.7 18.2 18.6 17.7 17.8 16.8C17 15.9 16.6 14.7 16.6 13.1V6.8H19V12.9C19 13.9 19.2 14.7 19.6 15.2C20 15.7 20.6 16 21.4 16C22.2 16 22.9 15.7 23.4 15.1C23.9 14.5 24.2 13.7 24.2 12.7V6.8H26.6V18H24.3L24.1 16.4C23.7 16.9 23.2 17.3 22.6 17.6C22 17.9 21.6 18.2 21.1 18.2Z" fill="#009688" />
            <path d="M35 8.9C34.4 8.9 33.9 9.1 33.5 9.5C33.1 9.9 32.8 10.5 32.7 11.3H37.2C37.2 10.5 37 9.9 36.6 9.5C36.2 9.1 35.7 8.9 35 8.9Z" fill="#009688" />
            <path d="M35 18.2C33.3 18.2 32 17.7 31 16.7C30 15.7 29.5 14.3 29.5 12.5C29.5 10.7 30 9.3 30.9 8.3C31.8 7.3 33.1 6.8 34.7 6.8C36.3 6.8 37.5 7.3 38.4 8.2C39.3 9.1 39.7 10.4 39.7 12V12.9L32.6 12.9C32.7 13.8 33 14.5 33.5 15C34 15.5 34.7 15.7 35.5 15.7C36 15.7 36.5 15.6 37 15.4C37.5 15.2 37.9 14.9 38.3 14.6L39.4 16.3C38.9 16.7 38.3 17.1 37.6 17.4C36.8 17.9 36 18.2 35 18.2Z" fill="#009688" />
            <path d="M46.2 18.2C44.7 18.2 43.5 17.7 42.6 16.7C41.7 15.7 41.2 14.3 41.2 12.5C41.2 10.7 41.7 9.3 42.6 8.3C43.5 7.3 44.8 6.8 46.4 6.8C47.1 6.8 47.7 6.9 48.3 7.2C48.9 7.5 49.3 7.8 49.6 8.2L49.8 7H52.1V18H49.8L49.6 16.8C49.3 17.2 48.8 17.6 48.3 17.8C47.7 18.1 47 18.2 46.2 18.2ZM46.9 16C47.7 16 48.3 15.7 48.8 15.2C49.3 14.7 49.5 13.9 49.5 12.9V12.1C49.5 11.1 49.3 10.4 48.8 9.9C48.3 9.4 47.7 9.1 46.9 9.1C46.1 9.1 45.5 9.4 45 9.9C44.5 10.4 44.3 11.2 44.3 12.2C44.3 13.2 44.5 14 45 14.6C45.5 15.3 46.1 16 46.9 16Z" fill="#009688" />
            <path d="M55.3 18V6.8H57.6L57.8 8.5C58.1 7.9 58.6 7.4 59.2 7.1C59.8 6.8 60.5 6.6 61.3 6.6V9.1H60.5C59.7 9.1 59 9.3 58.5 9.7C58 10.1 57.7 10.8 57.7 11.8V18H55.3Z" fill="#009688" />
            <path fill="#009688" d="M88 2H90.4V18H88V2Z" />
            <path d="M94 18V6.8H96.3L96.5 8.5C96.8 7.9 97.3 7.4 97.9 7.1C98.5 6.8 99.2 6.6 100 6.6V9.1H99.2C98.4 9.1 97.7 9.3 97.2 9.7C96.7 10.1 96.4 10.8 96.4 11.8V18H94Z" fill="#009688" />
            <path d="M108.1 8.9C107.5 8.9 107 9.1 106.6 9.5C106.2 9.9 105.9 10.5 105.8 11.3H110.3C110.3 10.5 110.1 9.9 109.7 9.5C109.3 9.1 108.8 8.9 108.1 8.9Z" fill="#009688" />
            <path d="M108.1 18.2C106.4 18.2 105.1 17.7 104.1 16.7C103.1 15.7 102.6 14.3 102.6 12.5C102.6 10.7 103.1 9.3 104 8.3C104.9 7.3 106.2 6.8 107.8 6.8C109.4 6.8 110.6 7.3 111.5 8.2C112.4 9.1 112.8 10.4 112.8 12V12.9L105.7 12.9C105.8 13.8 106.1 14.5 106.6 15C107.1 15.5 107.8 15.7 108.6 15.7C109.1 15.7 109.6 15.6 110.1 15.4C110.6 15.2 111 14.9 111.4 14.6L112.5 16.3C112 16.7 111.4 17.1 110.7 17.4C109.9 17.9 109 18.2 108.1 18.2Z" fill="#009688" />
          </svg>
        </div>
        <div style={{ paddingBottom: 'env(safe-area-inset-bottom)', background: '#fafafa' }} />
      </div>

      <style jsx global>{`
        @keyframes slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        .animate-slide-up { animation: slide-up 0.28s cubic-bezier(0.32,0.72,0,1) forwards; }
      `}</style>
    </>
  );
}
